import { Piece } from './Piece';

export class Board {
  private pieces: Piece[] = [];
  private grid: Piece[][] = [];
  private rows: number;
  private cols: number;
  private pieceWidth: number;
  private pieceHeight: number;
  private _offsetX: number;
  private _offsetY: number;
  private _boardWidth: number;
  private _boardHeight: number;

  get offsetX(): number { return this._offsetX; }
  get offsetY(): number { return this._offsetY; }
  get boardWidth(): number { return this._boardWidth; }
  get boardHeight(): number { return this._boardHeight; }

  constructor(
    rows: number,
    cols: number,
    boardWidth: number,
    boardHeight: number,
    offsetX: number,
    offsetY: number
  ) {
    this.rows = rows;
    this.cols = cols;
    this._boardWidth = boardWidth;
    this._boardHeight = boardHeight;
    this.pieceWidth = boardWidth / cols;
    this.pieceHeight = boardHeight / rows;
    this._offsetX = offsetX;
    this._offsetY = offsetY;
  }

  initialize(): void {
    this.pieces = [];
    this.grid = [];

    for (let row = 0; row < this.rows; row++) {
      this.grid[row] = [];
      for (let col = 0; col < this.cols; col++) {
        const correctX = this.offsetX + col * this.pieceWidth;
        const correctY = this.offsetY + row * this.pieceHeight;

        const piece = new Piece(
          row,
          col,
          correctX,
          correctY,
          this.pieceWidth,
          this.pieceHeight
        );

        this.pieces.push(piece);
        this.grid[row][col] = piece;
      }
    }
  }

  resize(newBoardWidth: number, newBoardHeight: number, newOffsetX: number, newOffsetY: number): void {
    // Store old dimensions before updating
    const oldOffsetX = this._offsetX;
    const oldOffsetY = this._offsetY;
    const oldBoardWidth = this._boardWidth;
    const oldBoardHeight = this._boardHeight;

    // Calculate scaling factors
    const scaleX = newBoardWidth / oldBoardWidth;
    const scaleY = newBoardHeight / oldBoardHeight;

    // Update board dimensions
    this._boardWidth = newBoardWidth;
    this._boardHeight = newBoardHeight;
    this._offsetX = newOffsetX;
    this._offsetY = newOffsetY;
    this.pieceWidth = newBoardWidth / this.cols;
    this.pieceHeight = newBoardHeight / this.rows;

    // Update all piece positions and sizes
    for (const piece of this.pieces) {
      // Calculate position relative to old board origin
      const relativeX = piece.currentX - oldOffsetX;
      const relativeY = piece.currentY - oldOffsetY;

      // Scale the relative position and add new offset
      const newX = newOffsetX + relativeX * scaleX;
      const newY = newOffsetY + relativeY * scaleY;

      // Update the piece
      piece.currentX = newX;
      piece.currentY = newY;
      piece.width = this.pieceWidth;
      piece.height = this.pieceHeight;

      // Also update correct position
      piece.correctX = newOffsetX + piece.col * this.pieceWidth;
      piece.correctY = newOffsetY + piece.row * this.pieceHeight;
    }
  }

  shuffle(): void {
    // Keep shuffling until we get a puzzle that isn't already solved
    let attempts = 0;
    const maxAttempts = 10; // Prevent infinite loop

    do {
      const flatPieces = [...this.pieces];
      for (let i = flatPieces.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [flatPieces[i], flatPieces[j]] = [flatPieces[j], flatPieces[i]];
      }

      let index = 0;
      for (let row = 0; row < this.rows; row++) {
        for (let col = 0; col < this.cols; col++) {
          const piece = flatPieces[index++];
          const gridX = this.offsetX + col * this.pieceWidth;
          const gridY = this.offsetY + row * this.pieceHeight;
          piece.setGridPosition(row, col, gridX, gridY);
          this.grid[row][col] = piece;
        }
      }

      attempts++;
    } while (this.isSolved() && attempts < maxAttempts);

    // If still solved after max attempts (extremely unlikely), just shuffle one more time
    if (this.isSolved()) {
      console.warn('Shuffle resulted in solved puzzle after max attempts');
    }
  }

  getPieceAt(x: number, y: number): Piece | null {
    for (let i = this.pieces.length - 1; i >= 0; i--) {
      if (this.pieces[i].containsPoint(x, y)) {
        return this.pieces[i];
      }
    }
    return null;
  }

  swapPieces(piece1: Piece, piece2: Piece): void {
    const grid1Row = piece1.gridRow;
    const grid1Col = piece1.gridCol;
    const grid2Row = piece2.gridRow;
    const grid2Col = piece2.gridCol;

    const pos1X = piece1.currentX;
    const pos1Y = piece1.currentY;
    const pos2X = piece2.currentX;
    const pos2Y = piece2.currentY;

    this.grid[grid1Row][grid1Col] = piece2;
    this.grid[grid2Row][grid2Col] = piece1;

    piece1.setGridPosition(grid2Row, grid2Col, pos2X, pos2Y);
    piece2.setGridPosition(grid1Row, grid1Col, pos1X, pos1Y);
  }

  moveGroup(selectedGroup: Set<Piece>, rowOffset: number, colOffset: number): void {
    const selectedPieces = Array.from(selectedGroup);

    // Store original positions for all involved pieces
    const pieceToOriginalPos = new Map<Piece, { row: number; col: number }>();
    for (const piece of selectedPieces) {
      pieceToOriginalPos.set(piece, { row: piece.gridRow, col: piece.gridCol });
    }

    // Find pieces that will be displaced and store their positions
    const displacedPieces: Piece[] = [];
    const newPositionsOfSelected = new Set<string>();

    for (const piece of selectedPieces) {
      const originalPos = pieceToOriginalPos.get(piece)!;
      const newRow = originalPos.row + rowOffset;
      const newCol = originalPos.col + colOffset;
      newPositionsOfSelected.add(`${newRow},${newCol}`);

      const occupant = this.grid[newRow]?.[newCol];
      if (occupant && !selectedGroup.has(occupant)) {
        if (!pieceToOriginalPos.has(occupant)) {
          pieceToOriginalPos.set(occupant, { row: occupant.gridRow, col: occupant.gridCol });
          displacedPieces.push(occupant);
        }
      }
    }

    // Collect positions that will be vacated by the selected group
    const vacatedPositions: Array<{ row: number; col: number }> = [];
    for (const piece of selectedPieces) {
      const originalPos = pieceToOriginalPos.get(piece)!;
      const posKey = `${originalPos.row},${originalPos.col}`;
      // A position is vacated if it won't be occupied by the selected group after the move
      if (!newPositionsOfSelected.has(posKey)) {
        vacatedPositions.push(originalPos);
      }
    }

    // Verify we have enough positions for all displaced pieces
    if (displacedPieces.length !== vacatedPositions.length) {
      console.error('Mismatch: displaced pieces =', displacedPieces.length, 'vacated positions =', vacatedPositions.length);
      console.error('Selected group size:', selectedGroup.size);
      return; // Don't perform move if sizes don't match
    }

    // Clear all affected grid positions
    for (const [, pos] of pieceToOriginalPos) {
      this.grid[pos.row][pos.col] = null as any;
    }

    // Move selected group to their new positions
    for (const piece of selectedPieces) {
      const originalPos = pieceToOriginalPos.get(piece)!;
      const newRow = originalPos.row + rowOffset;
      const newCol = originalPos.col + colOffset;

      if (newRow >= 0 && newRow < this.rows && newCol >= 0 && newCol < this.cols) {
        const newX = this.offsetX + newCol * this.pieceWidth;
        const newY = this.offsetY + newRow * this.pieceHeight;
        piece.setGridPosition(newRow, newCol, newX, newY);
        this.grid[newRow][newCol] = piece;
      }
    }

    // Move displaced pieces to vacated positions
    for (let i = 0; i < displacedPieces.length; i++) {
      const piece = displacedPieces[i];
      const pos = vacatedPositions[i];
      const newX = this.offsetX + pos.col * this.pieceWidth;
      const newY = this.offsetY + pos.row * this.pieceHeight;
      piece.setGridPosition(pos.row, pos.col, newX, newY);
      this.grid[pos.row][pos.col] = piece;
    }
  }

  swapGroups(selectedGroup: Set<Piece>, targetGroup: Set<Piece>): void {
    const selectedPieces = Array.from(selectedGroup);
    const targetPieces = Array.from(targetGroup);

    // Calculate the offset to move the selected group
    const selectedRef = selectedPieces[0];
    const targetRef = targetPieces[0];

    const rowOffset = targetRef.gridRow - selectedRef.gridRow;
    const colOffset = targetRef.gridCol - selectedRef.gridCol;

    // Use the moveGroup method with the calculated offset
    this.moveGroup(selectedGroup, rowOffset, colOffset);
  }

  getPieces(): Piece[] {
    return this.pieces;
  }

  isSolved(): boolean {
    return this.pieces.every(piece => piece.isInCorrectPosition());
  }

  getCompletionPercentage(): number {
    const correctCount = this.pieces.filter(piece => piece.isInCorrectPosition()).length;
    return (correctCount / this.pieces.length) * 100;
  }

  getConnectedGroup(piece: Piece): Set<Piece> {
    const group = new Set<Piece>();
    const toCheck = [piece];

    while (toCheck.length > 0) {
      const current = toCheck.pop()!;
      if (group.has(current)) continue;

      group.add(current);

      // Check all four directions for correctly adjacent pieces
      const neighbors = [
        { neighbor: current.gridRow > 0 ? this.grid[current.gridRow - 1][current.gridCol] : null,
          correctRow: current.row - 1, correctCol: current.col },
        { neighbor: current.gridCol < this.cols - 1 ? this.grid[current.gridRow][current.gridCol + 1] : null,
          correctRow: current.row, correctCol: current.col + 1 },
        { neighbor: current.gridRow < this.rows - 1 ? this.grid[current.gridRow + 1][current.gridCol] : null,
          correctRow: current.row + 1, correctCol: current.col },
        { neighbor: current.gridCol > 0 ? this.grid[current.gridRow][current.gridCol - 1] : null,
          correctRow: current.row, correctCol: current.col - 1 },
      ];

      for (const { neighbor, correctRow, correctCol } of neighbors) {
        if (neighbor &&
            neighbor.row === correctRow &&
            neighbor.col === correctCol &&
            !group.has(neighbor)) {
          toCheck.push(neighbor);
        }
      }
    }

    return group;
  }

  draw(ctx: CanvasRenderingContext2D, image: HTMLImageElement): void {
    const sourceWidth = image.width / this.cols;
    const sourceHeight = image.height / this.rows;

    this.pieces.forEach(piece => {
      // Find neighbors in the current grid
      const neighbors = {
        top: piece.gridRow > 0 ? this.grid[piece.gridRow - 1][piece.gridCol] : undefined,
        right: piece.gridCol < this.cols - 1 ? this.grid[piece.gridRow][piece.gridCol + 1] : undefined,
        bottom: piece.gridRow < this.rows - 1 ? this.grid[piece.gridRow + 1][piece.gridCol] : undefined,
        left: piece.gridCol > 0 ? this.grid[piece.gridRow][piece.gridCol - 1] : undefined,
      };

      piece.draw(ctx, image, sourceWidth, sourceHeight, neighbors);
    });
  }
}
